import { useEffect, useState } from "react";
import { api, apiError, getSessionTokenForTest } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { Download, Users, Zap, Filter } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtINR } from "@/lib/format";

/**
 * Payroll — PF/ESIC-ready monthly salary statement.
 *
 * Lists the merged employee master (attendance names + payroll salary
 * structure), lets manager edit designation / salary / KYC per person, and
 * downloads the exact 39-column .xlsx that Mili hands to PF/ESIC.
 */
export default function Payroll() {
  const { user } = useAuth();
  const canEdit = ["owner", "developer", "admin", "manager"].includes(user?.role);

  const today = new Date();
  const [month, setMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
  );
  const [scope, setScope] = useState("all");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/payroll/employees", { params: { scope } });
      setRows(r.data || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, [scope]); // eslint-disable-line

  const seed = async () => {
    if (!window.confirm("Import the merged employee master (113 rows: 111 from attendance + 33 from June payroll)?\n\nExisting KYC and any manually-edited salary fields are preserved."))
      return;
    setBusy(true);
    try {
      const r = await api.post("/payroll/seed");
      toast.success(`Employees seeded — ${r.data.inserted} new, ${r.data.updated} updated`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = new URL(
        `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/payroll/statement.xlsx`
      );
      url.searchParams.set("month", month);
      url.searchParams.set("scope", scope);
      const token = getSessionTokenForTest();
      const res = await fetch(url.toString(), {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `VAS_Salary_${month}_${scope}.xlsx`;
      link.click();
      toast.success("Salary statement downloaded ✓");
    } catch (e) {
      toast.error(apiError(e) || "Download failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async (code, patch) => {
    try {
      await api.patch(`/payroll/employees/${code}`, patch);
      toast.success("Saved");
      load();
      setEditing(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const vasCount = rows.filter((r) => r.entity === "VAS").length;
  const corpCount = rows.filter((r) => r.entity === "Corporate").length;
  const payrollCount = rows.filter((r) => r.in_payroll).length;

  return (
    <div className="space-y-4" data-testid="payroll-root">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Payroll — Salary Statement
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Exports the exact PF/ESIC-ready workbook Mili has been filing every month
            (title block + 39 columns + totals). Built from Attendance + salary master.
          </p>
        </div>
        {(user?.role === "owner" || user?.role === "admin") && (
          <div className="flex gap-2 flex-wrap">
            {user?.role === "owner" && (
              <Button data-testid="seed-employees-btn" variant="outline" onClick={seed} disabled={busy} className="border-slate-300">
                <Zap size={14} className="mr-1" /> Seed employees (one-shot)
              </Button>
            )}
            <Button data-testid="backfill-pins-btn" variant="outline" disabled={busy}
                    onClick={async () => {
                      try { setBusy(true); const r = await api.post("/payroll/employees/backfill_pins_and_categories"); toast.success(`Backfilled ${r.data.updated} employees with PIN + Category`); load(); }
                      catch (e) { toast.error(apiError(e)); }
                      finally { setBusy(false); }
                    }} className="border-slate-300">
              <Zap size={14} className="mr-1" /> Assign PINs + Categories
            </Button>
          </div>
        )}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <div className="p-3 border-2 border-slate-300 rounded-sm bg-white">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Employees</div>
          <div className="font-bold text-2xl">{rows.length}</div>
        </div>
        <div className="p-3 border-2 border-slate-300 rounded-sm bg-white">
          <div className="text-xs text-slate-500 uppercase tracking-wider">VAS pump</div>
          <div className="font-bold text-2xl">{vasCount}</div>
        </div>
        <div className="p-3 border-2 border-slate-300 rounded-sm bg-white">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Corporate</div>
          <div className="font-bold text-2xl">{corpCount}</div>
        </div>
        <div className="p-3 border-2 border-slate-300 rounded-sm bg-white">
          <div className="text-xs text-slate-500 uppercase tracking-wider">On payroll</div>
          <div className="font-bold text-2xl">{payrollCount}</div>
        </div>
      </div>

      {/* Export bar */}
      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="payroll-month"
              className="block h-10 border border-slate-300 rounded-sm px-3 bg-white w-full"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1">
              <Filter size={12} /> Scope
            </label>
            <div className="flex gap-1 mt-1">
              {[
                ["all", "All"],
                ["vas", "VAS Pump"],
                ["corporate", "Corporate"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`payroll-scope-${v}`}
                  onClick={() => setScope(v)}
                  className={
                    "flex-1 h-10 border-2 font-bold text-xs rounded-sm " +
                    (scope === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                  }
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Button
            data-testid="payroll-download-btn"
            onClick={download}
            disabled={busy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 md:col-span-2"
          >
            <Download size={14} className="mr-1" /> {busy ? "Preparing…" : "Download salary statement (.xlsx)"}
          </Button>
        </div>
      </Card>

      {/* Employee table */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
          <Users size={16} /> Employees — {scope === "all" ? "all" : scope}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-slate-300">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-2 py-1 border-b border-slate-300">Code</th>
                <th className="text-left px-2 py-1 border-b border-slate-300" title="PIN itself is never displayed. Green ✓ = set, red ✗ = missing.">PIN?</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Name</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Category</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Designation</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Entity</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Basic</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Revised</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">Total allowns</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">PT</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">PF</th>
                <th className="text-right px-2 py-1 border-b border-slate-300">ESIC</th>
                <th className="text-left px-2 py-1 border-b border-slate-300">Note</th>
                {canEdit && <th className="text-left px-2 py-1 border-b border-slate-300"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const s = e.salary || {};
                const totalAllowns = ["food_allowns", "breakfast_allowns", "hra", "special_allowns", "travelling_allowns", "washing_charge", "room_rent"]
                  .reduce((a, k) => a + Number(s[k] || 0), 0);
                return (
                  <tr key={e.code} className={"border-b border-slate-200 hover:bg-slate-50 " + (e.active === false ? "opacity-50 italic" : "")}>
                    <td className="px-2 py-1 font-mono">{e.code}</td>
                    <td className="px-2 py-1 text-center">
                      {e.has_pin
                        ? <span className="text-emerald-700 font-bold" title="PIN set (never displayed)">✓</span>
                        : <span className="text-red-600 font-bold" title="No PIN yet — click Edit and hit 'Reset PIN'">✗</span>}
                    </td>
                    <td className="px-2 py-1 font-bold">{e.name}</td>
                    <td className="px-2 py-1 text-[11px]">{e.category || <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1">{e.designation}</td>
                    <td className="px-2 py-1">
                      <span className={
                        "text-[10px] px-1.5 py-0.5 rounded-sm border font-semibold " +
                        (e.entity === "Corporate" ? "border-purple-300 bg-purple-50 text-purple-800" : "border-cyan-300 bg-cyan-50 text-cyan-800")
                      }>
                        {e.entity}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right">{fmtINR(s.basic)}</td>
                    <td className="px-2 py-1 text-right font-bold">{fmtINR(s.revised_basic)}</td>
                    <td className="px-2 py-1 text-right">{fmtINR(totalAllowns)}</td>
                    <td className="px-2 py-1 text-right">{s.pt || 0}</td>
                    <td className="px-2 py-1 text-right">{fmtINR(s.pf)}</td>
                    <td className="px-2 py-1 text-right">{fmtINR(s.esic)}</td>
                    <td className="px-2 py-1 text-slate-500 text-[11px]">{e.match_note || ""}</td>
                    {canEdit && (
                      <td className="px-2 py-1">
                        <button
                          data-testid={`edit-emp-${e.code}`}
                          onClick={() => setEditing(e)}
                          className="text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit sheet */}
      {editing && (
        <Card className="p-4 border-2 border-amber-400 bg-amber-50 rounded-sm" data-testid="edit-emp-panel">
          <h3 className="font-bold text-slate-900 mb-2">
            Edit {editing.name} ({editing.code})
          </h3>
          <EditForm emp={editing} onCancel={() => setEditing(null)} onSave={(patch) => save(editing.code, patch)} />
        </Card>
      )}
    </div>
  );
}

function EditForm({ emp, onCancel, onSave }) {
  const [designation, setDesignation] = useState(emp.designation || "");
  const [entity, setEntity] = useState(emp.entity || "VAS");
  const [category, setCategory] = useState(emp.category || "Customer Attendant");
  const [active, setActive] = useState(emp.active !== false);
  const [hasPin, setHasPin] = useState(!!emp.has_pin);
  const [freshPin, setFreshPin] = useState("");  // shown ONCE after reset, then wiped from state on close
  const [doj, setDoj] = useState(emp.date_of_joining || "");
  const [aadhaar, setAadhaar] = useState(emp.aadhaar_no || "");
  const [phone, setPhone] = useState(emp.phone || "");
  const [bankAcc, setBankAcc] = useState(emp.bank_account_no || "");
  const [ifsc, setIfsc] = useState(emp.ifsc || "");
  const [salary, setSalary] = useState(emp.salary || {});

  const set = (k, v) => setSalary((s) => ({ ...s, [k]: Number(v) || 0 }));
  const CATS = ["Management", "Cashier", "Customer Attendant", "Housekeeper", "Corporate", "Other"];

  const regenPin = async () => {
    // Phase-3 A2: reveals plaintext PIN ONCE. Admin hands it to the
    // employee; nobody can retrieve it again after this dialog closes.
    if (!window.confirm(`Reset ${emp.name}'s PIN? The new PIN will show ONCE — copy it before closing this dialog.`)) return;
    try {
      const r = await api.post(`/payroll/employees/${emp.code}/regenerate_pin`);
      setFreshPin(r.data.pin);
      setHasPin(true);
      toast.success("New PIN generated. Hand it to the employee — it won't be shown again.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
      <F label="Designation"><Input value={designation} onChange={(e) => setDesignation(e.target.value)} className="h-9" /></F>
      <F label="Category">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-full border border-slate-300 rounded-sm px-2 bg-white" data-testid="edit-emp-category">
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </F>
      <F label="Entity">
        <div className="flex gap-1">
          {["VAS", "Corporate"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setEntity(v)}
              className={"flex-1 h-9 border-2 font-bold text-xs rounded-sm " +
                (entity === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}
            >
              {v}
            </button>
          ))}
        </div>
      </F>
      <F label={hasPin ? "PIN (already set)" : "PIN (not set)"}>
        <div className="space-y-1">
          <div className="flex gap-1 items-center">
            <div className={"flex-1 h-9 border border-slate-300 rounded-sm px-2 flex items-center text-xs " + (hasPin ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700")} data-testid="edit-emp-pin-status">
              {hasPin ? "✓ PIN set — never displayed" : "✗ No PIN — reset to generate"}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={regenPin} className="border-slate-300 h-9 text-xs" data-testid="edit-emp-reset-pin">Reset</Button>
          </div>
          {freshPin && (
            <div className="p-2 border-2 border-amber-500 bg-amber-50 rounded-sm text-xs" data-testid="edit-emp-fresh-pin">
              <div className="font-bold text-amber-900">New PIN — copy this NOW, it won&apos;t be shown again:</div>
              <div className="mt-1 font-mono text-2xl text-center tracking-[0.5em] bg-white border border-amber-400 rounded-sm py-1">{freshPin}</div>
            </div>
          )}
        </div>
      </F>
      <F label="Active?">
        <div className="flex gap-1">
          <button type="button" onClick={() => setActive(true)}
                  className={"flex-1 h-9 border-2 font-bold text-xs rounded-sm " + (active ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-slate-300")} data-testid="edit-emp-active-yes">Active</button>
          <button type="button" onClick={() => setActive(false)}
                  className={"flex-1 h-9 border-2 font-bold text-xs rounded-sm " + (!active ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-300")} data-testid="edit-emp-active-no">Deactivated</button>
        </div>
      </F>
      <F label="Date of joining"><Input type="date" value={doj || ""} onChange={(e) => setDoj(e.target.value)} className="h-9" /></F>
      <F label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9" /></F>
      <F label="Aadhaar"><Input value={aadhaar} onChange={(e) => setAadhaar(e.target.value)} className="h-9" /></F>
      <F label="Bank a/c"><Input value={bankAcc} onChange={(e) => setBankAcc(e.target.value)} className="h-9" /></F>
      <F label="IFSC"><Input value={ifsc} onChange={(e) => setIfsc(e.target.value)} className="h-9" /></F>

      <div className="col-span-2 md:col-span-4 mt-1 text-xs font-bold uppercase tracking-wider text-slate-600 border-t border-slate-300 pt-2">
        Salary structure
      </div>
      {[
        ["basic", "Basic"], ["revised_basic", "Revised basic"],
        ["food_allowns", "Food"], ["breakfast_allowns", "Breakfast"],
        ["hra", "HRA"], ["special_allowns", "Special"],
        ["travelling_allowns", "Travelling"], ["washing_charge", "Washing"],
        ["room_rent", "Room rent"], ["ot_rate_per_hour", "OT ₹/hr"],
        ["pf", "PF"], ["pt", "PT"], ["esic", "ESIC"], ["mlwf", "MLWF"],
        ["loan_balance", "Loan balance"], ["adv_balance", "Adv balance"],
        ["loan_emi", "Loan EMI"], ["adv_emi", "Adv EMI"],
      ].map(([k, label]) => (
        <F key={k} label={label}>
          <Input type="number" step="0.01" value={salary[k] ?? 0} onChange={(e) => set(k, e.target.value)} className="h-9 num" />
        </F>
      ))}

      <div className="col-span-2 md:col-span-4 flex gap-2 mt-2">
        <Button
          onClick={() =>
            onSave({
              designation, entity, category, active,
              date_of_joining: doj || null,
              aadhaar_no: aadhaar, phone, bank_account_no: bankAcc, ifsc,
              salary,
            })
          }
          className="bg-slate-900 hover:bg-slate-800 text-white"
        >
          Save
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-slate-300">Cancel</Button>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">{label}</label>
      {children}
    </div>
  );
}
